/**
 * Fill form fields from the customer record selected in a customers_based dropdown.
 */

export class CustomersBasedFillHelper {
    static sourceToTargets = new Map();
    static formConfig = null;
    static delegationSetup = false;
  
    static setup(formConfig) {
      this.formConfig = formConfig;
      this.sourceToTargets.clear();
      if (!formConfig?.fields) return;
  
      formConfig.fields.forEach((field) => {
        const fill = field.customersBasedFill;
        if (!fill?.sourceFieldId || !fill?.customerField) return;
        let targets = this.sourceToTargets.get(fill.sourceFieldId);
        if (!targets) {
          targets = [];
          this.sourceToTargets.set(fill.sourceFieldId, targets);
        }
        targets.push({
          targetFieldId: field.id,
          customerField: fill.customerField,
          onlyIfEmpty: !!fill.onlyIfEmpty,
        });
      });
  
      if (this.sourceToTargets.size === 0) return;
  
      if (!this.delegationSetup) {
        document.addEventListener('change', (e) => this.onChange(e), true);
        this.delegationSetup = true;
      }
  
      this.runInitialSync();
    }
  
    static getSourceFieldConfig(sourceFieldId) {
      return this.formConfig?.fields?.find((f) => f.id === sourceFieldId);
    }
  
    static findOption(sourceFieldId, value) {
      const field = this.getSourceFieldConfig(sourceFieldId);
      if (!field?.customers_options || value === '' || value == null) return null;
      return field.customers_options.find((o) => String(o.value) === String(value)) || null;
    }
  
    static customerFromSelectOption(selectEl, value) {
      if (!selectEl || value === '' || value == null) return null;
      const opt = Array.from(selectEl.options || []).find(
        (o) => String(o.value) === String(value),
      );
      if (!opt) return null;
      const raw = opt.getAttribute('data-customer');
      if (!raw) return null;
      try {
        return JSON.parse(decodeURIComponent(raw));
      } catch (_) {
        return null;
      }
    }
  
    static resolveCustomer(sourceFieldId, selectedValue, selectEl = null) {
      const option = this.findOption(sourceFieldId, selectedValue);
      if (option?.customer && typeof option.customer === 'object') {
        return option.customer;
      }
      const fromDom = this.customerFromSelectOption(selectEl, selectedValue);
      if (fromDom) {
        return fromDom;
      }
      return option?.customer || {};
    }
  
    static setFieldValue(field, value) {
      if (!field) return;
      const str = value == null ? '' : String(value);
      if (field.itiInstance && typeof field.itiInstance.setNumber === 'function') {
        field.itiInstance.setNumber(str);
      } else {
        field.value = str;
        if (field.classList?.contains('phone-input') && str) {
          field.setAttribute('data-full-number', str);
        }
      }
      const inputEvt = new Event('input', { bubbles: true });
      const changeEvt = new Event('change', { bubbles: true });
      // Mark as a legitimate programmatic fill so the condition engine (which
      // ignores untrusted events) still re-evaluates conditions that depend on
      // this auto-filled value.
      inputEvt.tqProgrammaticFill = true;
      changeEvt.tqProgrammaticFill = true;
      field.dispatchEvent(inputEvt);
      field.dispatchEvent(changeEvt);
    }
  
    static fillFromSource(sourceFieldId, selectedValue, selectEl = null) {
      const targets = this.sourceToTargets.get(sourceFieldId);
      if (!targets?.length) return;
  
      const el =
        selectEl ||
        document.querySelector(`[name="${sourceFieldId}"], [id="${sourceFieldId}"]`);
      const customer = this.resolveCustomer(sourceFieldId, selectedValue, el);
  
      targets.forEach(({ targetFieldId, customerField, onlyIfEmpty }) => {
        const target = document.querySelector(`[name="${targetFieldId}"], [id="${targetFieldId}"]`);
        if (!target) return;
  
        const currentValue = String(target.value || '').trim();
        const resolvedValue = customer[customerField] ?? '';
  
        if (onlyIfEmpty && currentValue !== '') return;
  
        this.setFieldValue(target, resolvedValue);
      });
    }
  
    static onChange(e) {
      const fieldName = e.target?.name || e.target?.id;
      if (!fieldName || !this.sourceToTargets.has(fieldName)) return;
      this.fillFromSource(fieldName, e.target.value, e.target);
    }
  
    static runInitialSync() {
      this.sourceToTargets.forEach((_, sourceFieldId) => {
        const el = document.querySelector(`[name="${sourceFieldId}"], [id="${sourceFieldId}"]`);
        if (el?.value) {
          this.fillFromSource(sourceFieldId, el.value, el);
        }
      });
    }
  }